/**
 * Workspace settings > Usage (DEC-198, DEC-199), shaped like the Claude app's usage page:
 * one month first (spend, scans, tokens), then spend per month, then who it was for
 * (`UsageCard`, DEC-196). Every figure comes from `GET /usage`. The API has no daily
 * series, so the chart is monthly and says so rather than looking like a daily chart.
 *
 * DEC-199: one selected month drives the tiles, the highlighted bar and the member table,
 * so no two figures on the page describe different periods. Clicking a bar selects it.
 * No em-dashes (DEC-069).
 */
import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getUsage } from '../../api/usage'
import type { UsageBucket } from '../../types/api'
import { UsageCard } from './UsageCard'

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function tokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`
  return String(n)
}

/** "2026-10" as "Oct 2026", or "Oct" when the year is implied by the row. */
function monthLabel(key: string, withYear = true): string {
  const [y, m] = key.split('-').map(Number)
  if (!y || !m) return key
  const d = new Date(Date.UTC(y, m - 1, 1))
  return d.toLocaleString('en-US', { month: 'short', year: withYear ? 'numeric' : undefined, timeZone: 'UTC' })
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-panel border border-hairline rounded-md px-4 py-3 min-w-0">
      <div className="text-[11px] text-muted">{label}</div>
      <div className="text-[22px] font-medium text-ink tabular-nums leading-tight mt-1">{value}</div>
    </div>
  )
}

function MonthlyBars({
  months,
  current,
  onPick,
}: {
  months: UsageBucket[]
  current: string
  onPick: (key: string) => void
}) {
  const shown = [...months].sort((a, b) => a.key.localeCompare(b.key)).slice(-12)
  const max = Math.max(...shown.map((b) => b.estimated_cost), 0)
  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4" data-testid="usage-by-month">
      <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
        Spend per month
      </div>
      <p className="px-4 pt-3 m-0 text-[11px] text-muted">
        Monthly totals. TDIA does not record spend per day yet. Pick a month to see it below.
      </p>
      <div className="px-4 py-[14px]">
        {shown.length === 0 || max === 0 ? (
          <p className="m-0 text-[12px] text-muted">No AI spend recorded yet.</p>
        ) : (
          <ul className="m-0 p-0 list-none flex items-end gap-2 h-[120px]" aria-label="Estimated AI spend per month">
            {shown.map((b) => (
              <li key={b.key} className="flex-1 min-w-0 h-full">
                <button
                  type="button"
                  onClick={() => onPick(b.key)}
                  aria-pressed={b.key === current}
                  aria-label={`${monthLabel(b.key)}: ${money(b.estimated_cost)}, ${b.scans} ${b.scans === 1 ? 'scan' : 'scans'}`}
                  className="w-full h-full flex flex-col items-center justify-end gap-1 rounded-sm hover:bg-hover-hue focus-visible:shadow-focus focus:outline-none"
                >
                  <span className="text-[10px] text-muted tabular-nums whitespace-nowrap">{money(b.estimated_cost)}</span>
                  <span
                    className={`w-full max-w-[36px] rounded-t-sm ${b.key === current ? 'bg-accent' : 'bg-accent/40'}`}
                    style={{ height: `${Math.max(2, (b.estimated_cost / max) * 80)}px` }}
                  />
                  <span className={`text-[10px] whitespace-nowrap ${b.key === current ? 'text-ink font-semibold' : 'text-faint'}`}>
                    {monthLabel(b.key, false)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export function UsageSection() {
  // undefined = this month, as the server counts it.
  const [picked, setPicked] = useState<string | undefined>(undefined)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['usage', picked ?? 'current'],
    queryFn: () => getUsage(picked),
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  })

  const month = data?.by_month.find((b) => b.key === data.current_month)
  const thisMonth = picked === undefined

  return (
    <>
      {isLoading ? (
        <p className="text-[13px] text-muted mb-4">Loading usage…</p>
      ) : isError || !data ? (
        <p className="text-[13px] text-muted mb-4">Usage could not be loaded.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[12px] font-semibold text-body">
              {monthLabel(data.current_month)}
              {thisMonth ? ' so far' : ''}
            </span>
            {!thisMonth && (
              <button type="button" onClick={() => setPicked(undefined)} className="text-[12px] text-accent-text hover:underline">
                Back to this month
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="usage-this-month">
            <Stat label="Estimated AI spend" value={money(month?.estimated_cost ?? 0)} />
            <Stat label="Scans" value={String(month?.scans ?? 0)} />
            <Stat label="Tokens" value={tokens(month?.tokens ?? 0)} />
          </div>
          <MonthlyBars months={data.by_month} current={data.current_month} onPick={setPicked} />
          <UsageCard data={data} monthLabel={monthLabel(data.current_month)} />
        </>
      )}
    </>
  )
}
